﻿import { FormEvent, useEffect, useMemo, useState, type CSSProperties } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { api } from "../app/api";
import { getAuthToken, setAuthToken, type AuthUser } from "../app/auth";
import { resolveHostSlugFromHostname } from "../app/tenant-host";
import { applyUiFrameworkMode } from "../app/ui-framework";
import { loadUiSettings, type OwnerThemeSettings } from "../app/ui-settings";

const adminLoginBackgroundUrl = new URL("../assets/Hintergrundgrafik.png", import.meta.url).toString();

type Props = {
  onAuthenticated: (user: AuthUser) => void;
};

type AdminBranding = {
  firmenname?: string;
  email?: string;
  logoDataUrl?: string;
  uiFramework?: "classic" | "mockup_v2";
  theme: OwnerThemeSettings;
};

const loadAdminBranding = (): AdminBranding => {
  const ui = loadUiSettings();
  return {
    firmenname: ui.management.firmenname,
    email: ui.management.email,
    logoDataUrl: ui.ownerLogoDataUrl,
    uiFramework: ui.management.uiFramework,
    theme: ui.ownerTheme
  };
};

const themeVars = (theme: OwnerThemeSettings): CSSProperties =>
  ({
    "--bg": theme.bg,
    "--surface": theme.surface,
    "--line": theme.line,
    "--text": theme.text,
    "--primary": theme.primary,
    "--owner-gradient-from": theme.gradientFrom,
    "--owner-gradient-to": theme.gradientTo,
    "--owner-gradient-enabled": theme.gradientEnabled ? "1" : "0",
    "--owner-gradient-strength": theme.gradientEnabled ? "100%" : "0%",
    "--admin-login-bg-image-enabled": theme.adminLoginBackgroundImageEnabled !== false ? "1" : "0",
    "--admin-login-bg-image": `url("${adminLoginBackgroundUrl}")`
  }) as CSSProperties;

export const AdminLoginPage = ({ onAuthenticated }: Props) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [rememberMe, setRememberMe] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const currentYear = new Date().getFullYear();
  const sessionExpired = Boolean((location.state as { sessionExpired?: boolean } | null)?.sessionExpired);

  const localBranding = useMemo(() => loadAdminBranding(), []);
  const brandingQuery = useQuery({
    queryKey: ["ui-settings-admin-login"],
    queryFn: () => api.uiBranding(),
    retry: 1,
    staleTime: 60_000
  });

  const branding = useMemo(() => {
    const ui = brandingQuery.data;
    if (!ui) return localBranding;
    return {
      firmenname: ui.management.firmenname,
      email: ui.management.email,
      logoDataUrl: ui.ownerLogoDataUrl,
      uiFramework: ui.management.uiFramework,
      theme: ui.ownerTheme
    };
  }, [brandingQuery.data, localBranding]);
  const companyName = String(branding.firmenname || "ObjektVote").trim();

  useEffect(() => {
    applyUiFrameworkMode(branding.uiFramework === "mockup_v2" ? "mockup_v2" : "classic");
  }, [branding.uiFramework]);

  useEffect(() => {
    const host = window.location.hostname.toLowerCase();
    const hostSlug = resolveHostSlugFromHostname(host);
    const platformAliases =
      (import.meta as { env?: Record<string, string | undefined> }).env?.VITE_PLATFORM_PORTAL_ALIASES ?? "plattform,platform";
    const isPlatformHost = String(platformAliases)
      .split(",")
      .map((entry) => entry.trim().toLowerCase())
      .filter(Boolean)
      .includes(hostSlug);

    if (isPlatformHost) {
      document.title = "ObjektVote | Plattform";
      return;
    }

    const tenantLabel = String(branding.firmenname || "Mandant").trim();
    document.title = `ObjektVote | ${tenantLabel}`;
  }, [branding.firmenname]);

  useEffect(() => {
    if (!sessionExpired) return;
    setError("Ihre Sitzung ist abgelaufen. Bitte melden Sie sich erneut an.");
    navigate(location.pathname, { replace: true, state: null });
  }, [location.pathname, navigate, sessionExpired]);

  if (getAuthToken()) {
    return <Navigate to="/" replace />;
  }

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setError(null);
    setLoading(true);
    try {
      const result = await api.adminLogin({ username: username.trim(), password, rememberMe });
      setAuthToken(result.token);
      onAuthenticated(result.user);
      const next = (location.state as { from?: string } | undefined)?.from || "/";
      navigate(next, { replace: true });
    } catch (error) {
      const message = error instanceof Error ? error.message : "";
      const normalized = message.trim().toLowerCase();
      if (
        normalized.includes("api 401") ||
        normalized.includes("ungültig") ||
        normalized.includes("ungueltig") ||
        normalized.includes("invalid")
      ) {
        setError("Benutzername oder Passwort ist ungültig.");
      } else if (normalized.includes("api 503") || normalized.includes("server nicht verfügbar")) {
        setError("Server aktuell nicht verfügbar. Bitte in wenigen Minuten erneut versuchen.");
      } else if (message.trim()) {
        setError(message.trim());
      } else {
        setError("Anmeldung derzeit nicht möglich. Bitte Serververbindung prüfen.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <main
      className={`owner-auth-shell owner-auth-shell-centered admin-login-shell${
        branding.theme.adminLoginBackgroundImageEnabled !== false ? " admin-login-shell--bg-on" : ""
      }`}
      style={themeVars(branding.theme)}
    >
      <section className="panel owner-auth-combined-card admin-login-card">
        <div className="owner-auth-head">
          <div>
            <div className="owner-auth-kicker admin-login-kicker">
              <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <path d="M12 3.5 5 6v5.4c0 4.3 2.7 8.2 7 9.8 4.3-1.6 7-5.5 7-9.8V6l-7-2.5Z" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                <path d="M9.6 11.6v-1.1a2.4 2.4 0 1 1 4.8 0v1.1" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
                <rect x="8.8" y="11.6" width="6.4" height="5.2" rx="1.2" stroke="currentColor" strokeWidth="1.8" />
              </svg>
                  <span>Verwaltungsportal</span>
            </div>
            <h1 className="owner-auth-title admin-login-title">ObjektVote</h1>
            <p className="owner-auth-subtitle">Bitte mit Ihrem Benutzerkonto anmelden.</p>
          </div>
          {branding.logoDataUrl ? <img src={branding.logoDataUrl} alt="Firmenlogo" className="owner-auth-logo owner-auth-logo-right admin-login-logo" /> : null}
        </div>

        <hr className="owner-auth-divider" />

        <form onSubmit={submit} className="owner-auth-body admin-login-body" style={{ gap: 16 }}>
          <div className="form-field admin-login-field">
            <label className="admin-login-label">Benutzername</label>
            <div className="admin-login-input-wrap">
              <span className="admin-login-input-icon" aria-hidden="true">
                <svg viewBox="0 0 24 24" fill="none">
                  <path d="M12 12.4a4.4 4.4 0 1 0 0-8.8 4.4 4.4 0 0 0 0 8.8Z" stroke="currentColor" strokeWidth="1.8" />
                  <path d="M4 20.2a8 8 0 0 1 16 0" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
                </svg>
              </span>
              <input
                className="table-field admin-login-input"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="Benutzername eingeben"
                autoComplete="username"
                required
                autoFocus
              />
            </div>
          </div>

          <div className="form-field owner-auth-password-field admin-login-field">
            <label className="admin-login-label">Passwort</label>
            <div className="admin-login-input-wrap admin-login-password-wrap">
              <span className="admin-login-input-icon" aria-hidden="true">
                <svg viewBox="0 0 24 24" fill="none">
                  <rect x="4.2" y="10.5" width="15.6" height="10.5" rx="2" stroke="currentColor" strokeWidth="1.8" />
                  <path d="M8.4 10.5V7.8a3.6 3.6 0 1 1 7.2 0v2.7" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
                  <circle cx="12" cy="15.9" r="1" fill="currentColor" />
                </svg>
              </span>
              <input
                className="table-field admin-login-input"
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Passwort eingeben"
                autoComplete="current-password"
                required
              />
              <button
                type="button"
                className="owner-auth-eye-btn admin-login-eye-btn"
                onClick={() => setShowPassword((v) => !v)}
                aria-label={showPassword ? "Passwort verbergen" : "Passwort anzeigen"}
                title={showPassword ? "Passwort verbergen" : "Passwort anzeigen"}
              >
                {showPassword ? (
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                    <path
                      d="M2 12C3.8 7.8 7.5 5 12 5c4.5 0 8.2 2.8 10 7-1.8 4.2-5.5 7-10 7-4.5 0-8.2-2.8-10-7Z"
                      stroke="currentColor"
                      strokeWidth="1.8"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <circle cx="12" cy="12" r="3.2" stroke="currentColor" strokeWidth="1.8" />
                    <path d="M4 4l16 16" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
                  </svg>
                ) : (
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                    <path
                      d="M2 12C3.8 7.8 7.5 5 12 5c4.5 0 8.2 2.8 10 7-1.8 4.2-5.5 7-10 7-4.5 0-8.2-2.8-10-7Z"
                      stroke="currentColor"
                      strokeWidth="1.8"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <circle cx="12" cy="12" r="3.2" stroke="currentColor" strokeWidth="1.8" />
                  </svg>
                )}
              </button>
            </div>
          </div>

          {error ? <div className="error-text">{error}</div> : null}

          <label className="admin-login-remember">
            <input type="checkbox" checked={rememberMe} onChange={(e) => setRememberMe(e.target.checked)} />
            <span>Angemeldet bleiben</span>
          </label>

          <div className="form-actions admin-login-actions" style={{ justifyContent: "flex-start" }}>
            <button
              type="submit"
              className="admin-login-submit"
              disabled={loading || !username.trim() || !password.trim()}
            >
              <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <rect x="4.2" y="10.5" width="15.6" height="10.5" rx="2" stroke="currentColor" strokeWidth="1.8" />
                <path d="M8.4 10.5V7.8a3.6 3.6 0 1 1 7.2 0v2.7" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
                <circle cx="12" cy="15.9" r="1" fill="currentColor" />
              </svg>
              <span>{loading ? "Anmeldung läuft..." : "Anmelden"}</span>
            </button>
          </div>
        </form>

        <div className="owner-auth-footer">
          <div className="owner-auth-footer-row admin-login-footer-row">
            <div className="admin-login-contact">
              <span className="admin-login-contact-icon" aria-hidden="true">
                <svg viewBox="0 0 24 24" fill="none">
                  <rect x="3.6" y="5.2" width="16.8" height="13.6" rx="2.4" stroke="currentColor" strokeWidth="1.8" />
                  <path d="m5.6 7 6.4 5 6.4-5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </span>
              <div className="admin-login-contact-copy">
                <strong>{companyName}</strong>
                {branding.email ? <a href={`mailto:${branding.email}`}>{branding.email}</a> : null}
              </div>
            </div>
            <span className="owner-auth-copyright admin-login-copyright">
              {`© ${currentYear} Emig Immobilien`}
            </span>
          </div>
        </div>
      </section>
    </main>
  );
};



